/**
 * /classes/:classId — roster table for a class. Each student row is a
 * link to /students/:studentId where the radar chart lives.
 */
import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/OidcProvider";
import {
  canManageSchool,
  useClassHeatmap,
  useClassKpHeatmap,
  useClassStats,
  useClassWithRoster,
  useSetClassTeacherRole,
  useEligibleTeachers,
  useRegisterTeacher,
  useReplaceClassTeachers,
  useKpStats,
  useOrgMe,
  type KpHeatmap,
  type Student,
  useSubjects,
} from "../../api/school";
import { DistributionChart } from "../../components/DistributionChart";
import { LevelBadges } from "../../components/SubjectLevel";
import { downloadReportPdf } from "../reports/downloadReportPdf";
import { HeatmapTable } from "../../components/HeatmapTable";
import { KpHeatmapTable } from "../../components/KpHeatmapTable";
import { RadarChart } from "../../components/RadarChart";
import { AddStudentsModal } from "./AddStudentsModal";
import { ClassEditForm } from "./ClassEditForm";
import { STATUS_BADGE } from "../students/LifecyclePanel";

/** KP 诊断视图(E4/E5):双向摘要(最薄弱/掌握最好,后端算好的
 *  班级均值,证据不足的 KP 不参与结论)+ KP 热力图 + 导出 A4 PDF
 *  (复用 /reports 的 html2canvas+jspdf 链路)。 */
function KpDiagnosisView({
  className2,
  kpHeatmap,
  classId,
}: {
  className2: string;
  kpHeatmap: KpHeatmap;
  classId: string;
}) {
  const { t } = useTranslation();
  const kpStats = useKpStats(classId, true);
  const printRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState(false);

  const rows = (kpStats.data?.kps ?? []).filter((k) => k.avg_mastery !== null);
  const solid = rows.filter((k) => !k.low_evidence);
  const weakest = solid.slice(0, 3);
  const strongest = [...solid].reverse().slice(0, 3);

  async function exportPdf() {
    if (!printRef.current) return;
    setExporting(true);
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    try {
      await downloadReportPdf({
        element: printRef.current,
        filename: `${className2.replace(/\s+/g, "_")}_KP诊断_${new Date().toISOString().slice(0, 10)}`,
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="px-3 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {exporting ? t("classes.detail.kpExporting") : t("classes.detail.kpExport")}
        </button>
      </div>
      <div ref={printRef} className="bg-white">
        <div className="px-1 pb-3">
          <h3 className="text-base font-semibold text-slate-800">
            {t("classes.detail.kpReportTitle", { name: className2 })}
          </h3>
          <p className="text-xs text-slate-400">{new Date().toLocaleDateString()}</p>
        </div>
        {(weakest.length > 0 || strongest.length > 0) && (
          <div className="grid grid-cols-2 gap-3 mb-3 px-1">
            <div className="rounded-lg border border-rose-100 bg-rose-50/50 p-3">
              <div className="text-xs font-semibold text-rose-700 mb-1.5">
                {t("classes.detail.kpWeakest")}
              </div>
              {weakest.map((k) => (
                <div key={k.kp_id} className="flex justify-between text-xs text-slate-700 py-0.5">
                  <span>{k.name_zh}</span>
                  <span className="font-mono text-rose-600">{Math.round(k.avg_mastery!)}</span>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
              <div className="text-xs font-semibold text-emerald-700 mb-1.5">
                {t("classes.detail.kpStrongest")}
              </div>
              {strongest.map((k) => (
                <div key={k.kp_id} className="flex justify-between text-xs text-slate-700 py-0.5">
                  <span>{k.name_zh}</span>
                  <span className="font-mono text-emerald-600">{Math.round(k.avg_mastery!)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <KpHeatmapTable data={kpHeatmap} />
      </div>
    </div>
  );
}

export function ClassDetailPage() {
  const { t, i18n } = useTranslation();
  // 名册层级标签要显示学科名 —— 取一次学科表复用(Jarod 2026-08-27)
  const subjectsForLevels = (useSubjects().data ?? []).map((x) => ({ id: x.id, name: x.name }));
  const { classId } = useParams<{ classId: string }>();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && state.user.isAdmin;
  const orgMe = useOrgMe();
  const canManage = isAdmin || canManageSchool(orgMe.data);
  // 班主任可以给自己的班加学生(2026-09-10);科任老师不行。老师名单与
  // 角色切换仍只有园长/管理员能动。
  const isHomeroom = Boolean(
    classId && (orgMe.data as { homeroom_class_ids?: string[] } | undefined)?.homeroom_class_ids?.includes(classId),
  );
  const canAddStudents = canManage || isHomeroom;
  // 班级名称/年级同样由园长或本班班主任改(2026-09-13,之前完全没有改名入口)。
  const canEditClass = canManage || isHomeroom;
  const [editing, setEditing] = useState(false);
  const { data, isLoading, error } = useClassWithRoster(classId);
  const setRole = useSetClassTeacherRole(classId);
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const stats = useClassStats(classId);
  const [heatmapMode, setHeatmapMode] = useState<"skill" | "kp">("skill");
  const heatmap = useClassHeatmap(classId);
  const kpHeatmap = useClassKpHeatmap(classId, heatmapMode === "kp");
  const [showAdd, setShowAdd] = useState(false);

  // Used by AddStudentsModal to filter out already-enrolled students.
  // Memoized so the modal doesn't refilter on every render.
  const enrolledIds = useMemo(
    () => new Set((data?.students ?? []).map((s) => s.id)),
    [data?.students],
  );

  if (isLoading) return <p className="p-6 text-slate-500 italic">{t("common.loading")}</p>;
  if (error)
    return (
      <div className="m-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(error as Error).message}
      </div>
    );
  if (!data) return null;

  const radarLang: "zh" | "en" = i18n.language.toLowerCase().startsWith("zh") ? "zh" : "en";

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/classes" className="text-sm text-sky-600 hover:underline">
            {t("classes.detail.back")}
          </Link>
          {editing && classId ? (
            <div className="mt-2">
              <ClassEditForm
                classId={classId}
                initial={{ name: data.name, grade: data.grade, section: data.section }}
                onDone={() => setEditing(false)}
              />
            </div>
          ) : (
            <h1 className="mt-2 text-2xl font-semibold text-slate-800 flex items-center gap-2">
              {data.name}
              {canEditClass && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  title={t("classes.edit.button")}
                  aria-label={t("classes.edit.button")}
                  className="text-base font-normal text-slate-400 hover:text-sky-600"
                >
                  ✎
                </button>
              )}
            </h1>
          )}
          <p className="text-sm text-slate-500 mt-1">
            {t("classes.detail.studentsCount", { count: data.student_count, semester: data.semester })}
          </p>
        </div>
        <Link to={`/classes/${data.id}/grades`} className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 whitespace-nowrap" data-testid="class-grades-link">
          {t("classes.detail.grades")}
        </Link>
        <Link to={`/classes/${data.id}/conduct`} className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 whitespace-nowrap" data-testid="class-conduct-link">
          {t("classes.detail.conduct")}
        </Link>
        {canAddStudents && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 whitespace-nowrap"
          >
            {t("classes.detail.addStudents")}
          </button>
        )}
      </header>

      {/* 任课老师(Jarod 2026-08-11):学科跟老师走;⭐=班主任,管理员可点击切换,可添加老师。 */}
      {((data.teachers && data.teachers.length > 0) || canManage) && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-500">{t("classes.detail.teachersTitle")}</h3>
            {canManage && (
              <button
                type="button"
                onClick={() => setShowAddTeacher(true)}
                className="text-xs text-sky-600 hover:underline"
              >
                {t("classes.detail.addTeacher")}
              </button>
            )}
          </div>
          {(data.teachers?.length ?? 0) === 0 && (
            <p className="text-xs text-slate-400">{t("classes.detail.noTeachersYet")}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {data.teachers.map((ti) => (
              <div
                key={ti.teacher_id}
                className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 pl-1.5 pr-3 py-1 text-sm"
              >
                <button
                  type="button"
                  disabled={!canManage || setRole.isPending}
                  title={t(ti.role === "homeroom" ? "classes.detail.homeroom" : "classes.detail.makeHomeroom")}
                  onClick={() =>
                    canManage &&
                    setRole.mutate({
                      teacherId: ti.teacher_id,
                      role: ti.role === "homeroom" ? "teacher" : "homeroom",
                    })
                  }
                  className={`text-base leading-none ${ti.role === "homeroom" ? "" : "opacity-25"} ${canManage ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                >
                  ⭐
                </button>
                <span className="font-medium text-slate-800">{ti.name}</span>
                {ti.role === "homeroom" && (
                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">
                    {t("classes.detail.homeroom")}
                  </span>
                )}
                {ti.subjects.map((sub) => (
                  <span key={sub} className="text-[10px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-1">
                    {sub}
                  </span>
                ))}
              </div>
            ))}
          </div>
          {showAddTeacher && (
            <AddTeacherModal
              existing={(data.teachers ?? []).map((ti) => ti.teacher_id)}
              classId={classId!}
              onClose={() => setShowAddTeacher(false)}
            />
          )}
        </section>
      )}

      <section className="rounded-lg border border-slate-200 bg-white overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">{t("classes.detail.tableHeadStudent")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("classes.detail.tableHeadNameEn")}</th>
              <th className="text-right px-4 py-2 font-medium">{t("classes.detail.tableHeadOverall")}</th>
              <th className="text-left px-4 py-2 font-medium w-32">{t("common.statusLabel")}</th>
              <th className="px-4 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {data.students.map((s: Student) => (
              <RosterRow subjectsForLevels={subjectsForLevels} key={s.id} student={s} isAdmin={canManage} />
            ))}
          </tbody>
        </table>
      </section>

      {stats.data && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">
              {t("classes.detail.distributionTitle")}
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              {t("classes.detail.classAverage")}
              <span className="font-mono text-slate-800 ml-1">
                {stats.data.class_average ?? "—"}
              </span>
            </p>
            <DistributionChart distribution={stats.data.distribution} />
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">
              {t("classes.detail.skillAveragesTitle")}
            </h2>
            <RadarChart
              data={{
                axes: Object.entries(stats.data.skill_averages).map(([skill, score]) => ({
                  skill,
                  score,
                })),
                overall: stats.data.class_average,
              }}
              height={260}
              lang={radarLang}
            />
          </div>
        </section>
      )}

      {data.students.length > 0 && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <AttentionPanel students={data.students} />
          <TopPerformersPanel students={data.students} />
        </section>
      )}

      {heatmap.data && (
        <section className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
              {t("classes.detail.heatmapTitle")}
            </h2>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
              {(["skill", "kp"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setHeatmapMode(m)}
                  className={`px-3 py-1 ${
                    heatmapMode === m ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {t(`classes.detail.heatmapMode.${m}`)}
                </button>
              ))}
            </div>
          </div>
          {heatmapMode === "skill" ? (
            <HeatmapTable data={heatmap.data} />
          ) : kpHeatmap.data ? (
            <KpDiagnosisView className2={data.name} kpHeatmap={kpHeatmap.data} classId={classId!} />
          ) : (
            <p className="text-sm text-slate-400 italic">{t("common.loading")}</p>
          )}
        </section>
      )}

      {showAdd && classId && (
        <AddStudentsModal
          classId={classId}
          schoolId={data.school_id}
          classGrade={data.grade}
          classSection={data.section ?? null}
          alreadyEnrolled={enrolledIds}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}

// ── Auxiliary panels ───────────────────────────────────────────────

/** Students with overall < 60, sorted ascending. Caps at 5 rows. */
function AttentionPanel({ students }: { students: Student[] }) {
  const { t } = useTranslation();
  const struggling = students
    .filter((s) => s.english_profile.overall !== null && s.english_profile.overall < 60)
    .sort((a, b) => (a.english_profile.overall ?? 0) - (b.english_profile.overall ?? 0))
    .slice(0, 5);

  return (
    <div className="rounded-lg border border-red-100 bg-red-50/30 p-4">
      <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-3">
        {t("classes.detail.attentionTitle")}
      </h2>
      {struggling.length === 0 ? (
        <p className="text-xs text-slate-500 italic">{t("classes.detail.attentionAllGood")}</p>
      ) : (
        <ul className="space-y-2">
          {struggling.map((s) => (
            <li key={s.id}>
              <Link
                to={`/students/${s.id}`}
                className="flex items-center gap-3 text-sm hover:bg-red-100/40 rounded px-2 py-1.5"
              >
                <span className="text-slate-800 flex-1">
                  {s.name_zh}
                  {s.name_en && (
                    <span className="text-slate-500 text-xs ml-1">({s.name_en})</span>
                  )}
                </span>
                <span className="font-mono text-sm text-red-700">
                  {s.english_profile.overall}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Students with overall ≥ 85, sorted descending. Caps at 5 rows. */
function TopPerformersPanel({ students }: { students: Student[] }) {
  const { t } = useTranslation();
  const top = students
    .filter((s) => s.english_profile.overall !== null && s.english_profile.overall >= 85)
    .sort((a, b) => (b.english_profile.overall ?? 0) - (a.english_profile.overall ?? 0))
    .slice(0, 5);

  return (
    <div className="rounded-lg border border-emerald-100 bg-emerald-50/30 p-4">
      <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-3">
        {t("classes.detail.topPerformersTitle")}
      </h2>
      {top.length === 0 ? (
        <p className="text-xs text-slate-500 italic">{t("classes.detail.topPerformersEmpty")}</p>
      ) : (
        <ul className="space-y-2">
          {top.map((s) => (
            <li key={s.id}>
              <Link
                to={`/students/${s.id}`}
                className="flex items-center gap-3 text-sm hover:bg-emerald-100/40 rounded px-2 py-1.5"
              >
                <span className="text-slate-800 flex-1">
                  {s.name_zh}
                  {s.name_en && (
                    <span className="text-slate-500 text-xs ml-1">({s.name_en})</span>
                  )}
                </span>
                <span className="font-mono text-sm text-emerald-700">
                  {s.english_profile.overall}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Single roster row with a status dropdown. Inactive students render
 *  with reduced visual weight (per Jarod's 2026-06-04 demo feedback —
 *  easy at-a-glance signal for who's still showing up). */
function RosterRow({ student, isAdmin: canManage, subjectsForLevels }: { student: Student; isAdmin: boolean; subjectsForLevels?: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  void canManage;
  // 影响矩阵 §3.4: 休学行灰显 + 角标;状态变更走学生详情页的状态机
  // 操作区(表驱动),这里不再放旧的 active/inactive 下拉。
  const onLeave = student.status === "on_leave";

  return (
    <tr
      className={`border-t border-slate-100 hover:bg-slate-50 ${
        onLeave ? "opacity-50" : ""
      }`.trim()}
    >
      <td className="px-4 py-3 text-slate-800">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {student.name_zh}
          {/* 学科分层标签(Jarod 2026-08-27:有学科的地方都要带层级) */}
          <LevelBadges levels={student.subject_levels} subjects={subjectsForLevels} />
        </span>
      </td>
      <td className="px-4 py-3 text-slate-600">{student.name_en ?? "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-slate-700">
        {student.english_profile.overall ?? (
          <span className="text-slate-400">{t("classes.detail.notAssessed")}</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${STATUS_BADGE[student.status]?.cls ?? "bg-slate-100 text-slate-600"}`}>
          {STATUS_BADGE[student.status]?.label ?? student.status}
        </span>
        {onLeave && student.expected_return_date && (
          <span className="ml-1.5 text-[11px] text-amber-600 whitespace-nowrap">
            {t("lifecycle.expectedReturn", { date: student.expected_return_date })}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/students/${student.id}`}
          className="text-xs px-3 py-1 rounded bg-sky-50 text-sky-700 hover:bg-sky-100 whitespace-nowrap"
        >
          {t("classes.detail.viewStudent")}
        </Link>
      </td>
    </tr>
  );
}


/** 添加老师到班级(Jarod 2026-08-11)。可选列表 = 学校可指派老师;
 *  未注册为 school_teachers 的先幂等注册再挂班。PUT 全量替换,后端
 *  保留留任老师的班主任角色。 */
function AddTeacherModal({ existing, classId, onClose }: { existing: string[]; classId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const eligible = useEligibleTeachers();
  const registerTeacher = useRegisterTeacher();
  const replace = useReplaceClassTeachers(classId);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [err, setErr] = useState<string | null>(null);

  const candidates = (eligible.data ?? []).filter((e) => !e.teacher_id || !existing.includes(e.teacher_id));

  async function submit() {
    setErr(null);
    try {
      const newIds: string[] = [];
      for (const userId of picked) {
        const row = eligible.data?.find((e) => e.user_id === userId);
        if (row?.teacher_id) newIds.push(row.teacher_id);
        else {
          const r = await registerTeacher.mutateAsync(userId);
          newIds.push(r.teacher_id);
        }
      }
      await replace.mutateAsync([...existing, ...newIds]);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const busy = registerTeacher.isPending || replace.isPending;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800 mb-3">{t("classes.detail.addTeacher")}</h3>
        {eligible.isLoading ? (
          <p className="text-xs text-slate-500 italic">{t("common.loading")}</p>
        ) : candidates.length === 0 ? (
          <p className="text-xs text-slate-500">{t("classes.detail.noEligibleTeachers")}</p>
        ) : (
          <div className="max-h-60 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
            {candidates.map((e) => (
              <label key={e.user_id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={picked.has(e.user_id)}
                  onChange={() =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      if (next.has(e.user_id)) next.delete(e.user_id);
                      else next.add(e.user_id);
                      return next;
                    })
                  }
                />
                <span className="flex-1 truncate text-sm text-slate-800">
                  {e.display_name} <span className="text-xs text-slate-500">({e.email})</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {err && <p className="mt-2 text-xs text-rose-500">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm rounded border border-slate-200 text-slate-600">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || picked.size === 0}
            className="px-4 py-1.5 text-sm rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {busy ? t("classes.createModal.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
